"use client";

import React, { useState, useCallback, useRef, createContext, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CaretRightIcon, FunnelIcon } from "@phosphor-icons/react";
import { getCaseDisplayName } from "@/shared/functions";
import { MediaSourceShareProvider, useMediaSourceShare, type InjectedDrawerApi } from "./mediaSourceShare";
import { useCopyHistoryState, CopyHistoryListener, type CopyHistoryState } from "./recently-copied";
import { useResearchNotesState } from "./research-notes";
import { useCapturesState } from "./tools-captures";
import FavoritesList from "./favorites-list";
import { ToolsSharedCaseDataProvider, useOptionalToolsSharedCaseData } from "./tools-shared-case-data";
import type { SourceObject } from "@/shared/types";
import { FeaturesContext } from "@/app/contexts";
export type ToolsDrawerTabId = "sources" | "recently-copied" | "research-notes" | "captures";

/**
 * React Aria modal overlay (used by HeroUI Drawer) sets overflow/padding on the root while open.
 * If cleanup races Framer exit, the page can stay non-interactive. Only strip those inline styles
 * when no other dialog is still mounted (avoids breaking a real modal open underneath).
 */
function restoreDocumentInteractionIfNoModalsRemain() {
  if (typeof document === "undefined") return;
  if (document.querySelectorAll('[aria-modal="true"]').length > 0) return;
  document.documentElement.style.removeProperty("overflow");
  document.documentElement.style.removeProperty("padding-right");
  document.body.style.removeProperty("margin-top");
}
function scheduleRestoreDocumentInteractionIfNoModalsRemain() {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      restoreDocumentInteractionIfNoModalsRemain();
      setTimeout(restoreDocumentInteractionIfNoModalsRemain, 0);
    });
  });
}
type ToolsDrawerContextValue = {
  isOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  onOpenChange: (open: boolean) => void;
  activeTab: ToolsDrawerTabId;
  setActiveTab: (tab: ToolsDrawerTabId) => void;
  openDrawerToTab: (tab: ToolsDrawerTabId) => void;
  caseId: string | undefined;
  copyHistoryState: CopyHistoryState;
  /** For injection into MediaSourceShareProvider (avoids circular dependency). */
  injectDrawerApi: InjectedDrawerApi;
  /**
   * When the event modal is editing media, UnifiedSources registers a handler so the Captures tab
   * can apply a saved WebP (data URL) to the current slide — same as MediaDisplay's add-capture control.
   */
  registerApplyCaptureToEventMedia: (fn: ((dataUrl: string) => void) | null) => void;
  applyCaptureToEventMedia: (dataUrl: string) => boolean;
  /** Bumps when a capture was applied from the tools drawer (MediaDisplay uses this to show capture view). */
  captureApplyNonce: number;
  /** True while UnifiedSources (event modal) has registered apply — enables Captures tab "apply to slide". */
  eventMediaCaptureApplyReady: boolean;
  /**
   * When the event (or title) modal is editing sources, UnifiedSources registers this so Sources & Media
   * can push a drawer entry into the modal's selected-sources list (same as search + "Add selected").
   */
  registerAddSharedSourceToEvent: (fn: ((source: SourceObject) => void) | null) => void;
  addSharedSourceToEvent: (source: SourceObject) => boolean;
  eventSharedSourceAddReady: boolean;
  /** Event modal registers this so Research Notes / Recently Copied can set the Description field. */
  registerApplyTextToEventDescription: (fn: ((text: string) => void) | null) => void;
  applyTextToEventDescription: (text: string) => boolean;
  eventDescriptionApplyReady: boolean;
};
const ToolsDrawerContext = createContext<ToolsDrawerContextValue | null>(null);

/** Narrow subscription: only `captureApplyNonce` changes here, not on drawer open/tab/copy history updates. */
const ToolsDrawerCaptureApplyNonceContext = createContext(0);
export function useToolsDrawer() {
  return useContext(ToolsDrawerContext);
}
export function useToolsDrawerCaptureApplyNonce() {
  return useContext(ToolsDrawerCaptureApplyNonceContext);
}

/** Blurs app chrome while the tools drawer is open; keep outside the drawer shell (sibling under ToolsDrawerProvider). */
export function ToolsDrawerChrome({
  children
}: {
  children: React.ReactNode;
}) {
  const ctx = useToolsDrawer();
  return <div className={`transition-[filter] duration-300 ease-in-out ${ctx?.isOpen ? "blur-sm" : "blur-0"}`} data-sentry-component="ToolsDrawerChrome" data-sentry-source-file="tools-drawer.tsx">{children}</div>;
}
function ToolsDrawerShell({
  caseId
}: {
  caseId: string | undefined;
}) {
  const drawer = useToolsDrawer();
  const mediaShare = useMediaSourceShare();
  const sharedCase = useOptionalToolsSharedCaseData();
  const {
    featureFlags
  } = useContext(FeaturesContext) || {};
  const researchNotesState = useResearchNotesState(caseId);
  const capturesState = useCapturesState(caseId);
  const [mounted, setMounted] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const caseDisplayName = getCaseDisplayName(caseId);
  const handleCountsChange = useCallback((_total: number, _filtered: number, filtered: boolean) => {
    setIsFiltered(filtered);
  }, []);
  const isOpen = drawer?.isOpen ?? false;
  const closeDrawer = drawer?.closeDrawer;
  const copyHistoryState = drawer?.copyHistoryState;
  const reloadRemoteBundle = caseId && sharedCase?.caseId === caseId ? sharedCase.refetch : null;
  const refetchCopyHistory = copyHistoryState?.refetch;

  /** Latest refetch fns — hook return objects may be recreated each render; deps must stay stable. */
  const reloadRemoteBundleRef = useRef(reloadRemoteBundle);
  const refetchCopyHistoryRef = useRef(refetchCopyHistory);
  const mediaRefetchEntriesRef = useRef(mediaShare?.refetchEntries);
  const researchNotesRefetchRef = useRef(researchNotesState.refetch);
  const capturesRefetchRef = useRef(capturesState.refetch);
  reloadRemoteBundleRef.current = reloadRemoteBundle;
  refetchCopyHistoryRef.current = refetchCopyHistory;
  mediaRefetchEntriesRef.current = mediaShare?.refetchEntries;
  researchNotesRefetchRef.current = researchNotesState.refetch;
  capturesRefetchRef.current = capturesState.refetch;
  useEffect(() => {
    setMounted(true);
  }, []);

  /** Remote case: one shared bundle; refetch when the drawer opens only (not on tab change — avoids racing debounced persist). */
  useEffect(() => {
    if (!isOpen || !refetchCopyHistoryRef.current) return;
    if (!reloadRemoteBundleRef.current) return;
    void reloadRemoteBundleRef.current();
  }, [isOpen, caseId, !!reloadRemoteBundle]);

  /** Local storage: refresh all data when the drawer opens. */
  useEffect(() => {
    if (!isOpen || !refetchCopyHistoryRef.current) return;
    if (reloadRemoteBundleRef.current) return;
    mediaRefetchEntriesRef.current?.();
    refetchCopyHistoryRef.current();
    researchNotesRefetchRef.current();
    capturesRefetchRef.current();
  }, [isOpen, !!reloadRemoteBundle]);
  useEffect(() => {
    if (!isOpen || !closeDrawer) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      // HeroUI / React Aria modals set aria-modal="true"; let them handle Escape first.
      if (document.querySelector('[aria-modal="true"]')) return;
      e.preventDefault();
      closeDrawer();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closeDrawer]);

  /** Open/close can interact with other overlays; keep document styles consistent. */
  useEffect(() => {
    scheduleRestoreDocumentInteractionIfNoModalsRemain();
  }, [isOpen]);
  if (!drawer || !closeDrawer || !copyHistoryState) return null;
  if (!mounted) return null;
  return createPortal(<AnimatePresence>
      {isOpen && <>
          <motion.div key="drawer-backdrop" aria-hidden className="fixed inset-0 z-[4999]" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: "easeOut"
      }} onPointerDown={() => closeDrawer()} />
          <motion.div key="drawer-panel" role="dialog" aria-modal="false" aria-label="Tools" initial={{
        x: "100%"
      }} animate={{
        x: 0
      }} exit={{
        x: "100%"
      }} transition={{
        type: "spring",
        damping: 30,
        stiffness: 300
      }} className={`tools-drawer-base fixed z-[5000] flex flex-col overflow-hidden min-h-0 outline-none bg-gray-900 border border-white/20 border-r-0 rounded-tl-lg rounded-bl-lg shadow-lg !top-0 !right-0 !h-screen !max-h-screen transition-[max-width,min-width] duration-200 ease-in-out max-w-[95vw] min-w-[95vw] ${hasSelection ? "md:max-w-[50vw] md:min-w-[50vw]" : "sm:max-w-[320px] sm:min-w-[320px]"}`}>
            <header className="flex flex-row items-center justify-between gap-2 px-4 py-4 border-b border-white/20 shrink-0">
              <div className="flex flex-col">
                <span className="text-white font-bold text-xl">Favorites</span>
                {caseDisplayName && <span className="text-white/40 text-xs font-normal mt-0.5">{caseDisplayName}</span>}
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <button type="button" onClick={() => setShowFilters(p => !p)} className={`flex items-center justify-center p-1 rounded cursor-pointer transition-colors ${showFilters ? "text-white bg-white/10" : "text-white/70 hover:text-white hover:bg-white/10"} focus:outline-none focus:ring-2 focus:ring-white/30`} aria-label="Toggle filters">
                  <FunnelIcon size={20} weight={showFilters || isFiltered ? "fill" : "regular"} />
                </button>
                <button type="button" onClick={closeDrawer} className="flex items-center justify-center p-1 rounded text-white/70 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30 cursor-pointer" aria-label="Close drawer">
                  <CaretRightIcon size={20} weight="bold" />
                </button>
              </div>
            </header>
            <FavoritesList entries={mediaShare?.entries ?? []} setEntries={mediaShare?.setEntries ?? (() => {})} persistEntries={mediaShare?.persistEntries ?? (() => {})} caseId={mediaShare?.caseId} onAddToTimeline={mediaShare?.onAddToTimeline} addSharedSourceToEvent={drawer.addSharedSourceToEvent} eventSharedSourceAddReady={drawer.eventSharedSourceAddReady} notes={researchNotesState.notes} onAddNote={researchNotesState.addNote} onUpdateNote={researchNotesState.updateNote} onDeleteNote={researchNotesState.deleteNote} copyHistory={copyHistoryState.copyHistory} copiedEntryId={copyHistoryState.copiedEntryId} onCopyEntry={copyHistoryState.handleCopyEntryClick} onRemoveCopyEntry={copyHistoryState.handleRemoveEntry} captures={capturesState.captures} onRemoveCapture={capturesState.removeCapture} captureEnabled={!!featureFlags?.capture} applyTextToEventDescription={drawer.applyTextToEventDescription} eventDescriptionApplyReady={drawer.eventDescriptionApplyReady} showFilters={showFilters} onSelectionChange={setHasSelection} onCountsChange={handleCountsChange} />
          </motion.div>
        </>}
    </AnimatePresence>, document.body);
}
export function ToolsDrawerProvider({
  children,
  caseId,
  onAddToTimeline
}: {
  children: React.ReactNode;
  caseId?: string;
  onAddToTimeline?: (event: import("./answer-item").EventFromChat) => void;
}) {
  return <ToolsSharedCaseDataProvider caseId={caseId} data-sentry-element="ToolsSharedCaseDataProvider" data-sentry-component="ToolsDrawerProvider" data-sentry-source-file="tools-drawer.tsx">
      <ToolsDrawerProviderInner caseId={caseId} onAddToTimeline={onAddToTimeline} data-sentry-element="ToolsDrawerProviderInner" data-sentry-source-file="tools-drawer.tsx">
        {children}
      </ToolsDrawerProviderInner>
    </ToolsSharedCaseDataProvider>;
}

/** Must render under ToolsSharedCaseDataProvider so copy history can load/persist via sharedData API. */
function ToolsDrawerProviderInner({
  children,
  caseId,
  onAddToTimeline
}: {
  children: React.ReactNode;
  caseId?: string;
  onAddToTimeline?: (event: import("./answer-item").EventFromChat) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTabState] = useState<ToolsDrawerTabId>("sources");
  const copyHistoryState = useCopyHistoryState(caseId);
  const openDrawer = useCallback(() => setIsOpen(true), []);
  const closeDrawer = useCallback(() => setIsOpen(false), []);
  const toggleDrawer = useCallback(() => setIsOpen(prev => !prev), []);
  const onOpenChange = useCallback((open: boolean) => setIsOpen(open), []);
  const openDrawerToTab = useCallback((tab: ToolsDrawerTabId) => {
    setActiveTabState(tab);
    setIsOpen(true);
  }, []);
  const setActiveTab = useCallback((tab: ToolsDrawerTabId) => {
    setActiveTabState(tab);
  }, []);
  const applyCaptureToEventMediaRef = useRef<((dataUrl: string) => void) | null>(null);
  const [captureApplyNonce, setCaptureApplyNonce] = useState(0);
  const [eventMediaCaptureApplyReady, setEventMediaCaptureApplyReady] = useState(false);
  const registerApplyCaptureToEventMedia = useCallback((fn: ((dataUrl: string) => void) | null) => {
    applyCaptureToEventMediaRef.current = fn;
    setEventMediaCaptureApplyReady(fn != null);
  }, []);
  const applyCaptureToEventMedia = useCallback((dataUrl: string) => {
    const fn = applyCaptureToEventMediaRef.current;
    if (!fn) return false;
    fn(dataUrl);
    setCaptureApplyNonce(n => n + 1);
    return true;
  }, []);
  const addSharedSourceToEventRef = useRef<((source: SourceObject) => void) | null>(null);
  const [eventSharedSourceAddReady, setEventSharedSourceAddReady] = useState(false);
  const registerAddSharedSourceToEvent = useCallback((fn: ((source: SourceObject) => void) | null) => {
    addSharedSourceToEventRef.current = fn;
    setEventSharedSourceAddReady(fn != null);
  }, []);
  const addSharedSourceToEvent = useCallback((source: SourceObject) => {
    const fn = addSharedSourceToEventRef.current;
    if (!fn) return false;
    fn(source);
    return true;
  }, []);
  const applyTextToEventDescriptionRef = useRef<((text: string) => void) | null>(null);
  const [eventDescriptionApplyReady, setEventDescriptionApplyReady] = useState(false);
  const registerApplyTextToEventDescription = useCallback((fn: ((text: string) => void) | null) => {
    applyTextToEventDescriptionRef.current = fn;
    setEventDescriptionApplyReady(fn != null);
  }, []);
  const applyTextToEventDescription = useCallback((text: string) => {
    const fn = applyTextToEventDescriptionRef.current;
    if (!fn) return false;
    fn(text);
    return true;
  }, []);
  const injectDrawerApi: InjectedDrawerApi = React.useMemo(() => ({
    isOpen,
    openDrawer,
    closeDrawer,
    openDrawerToTab
  }), [isOpen, openDrawer, closeDrawer, openDrawerToTab]);
  const value: ToolsDrawerContextValue = React.useMemo(() => ({
    isOpen,
    openDrawer,
    closeDrawer,
    toggleDrawer,
    onOpenChange,
    activeTab,
    setActiveTab,
    openDrawerToTab,
    caseId,
    copyHistoryState,
    injectDrawerApi,
    registerApplyCaptureToEventMedia,
    applyCaptureToEventMedia,
    captureApplyNonce,
    eventMediaCaptureApplyReady,
    registerAddSharedSourceToEvent,
    addSharedSourceToEvent,
    eventSharedSourceAddReady,
    registerApplyTextToEventDescription,
    applyTextToEventDescription,
    eventDescriptionApplyReady
  }), [isOpen, openDrawer, closeDrawer, toggleDrawer, onOpenChange, activeTab, setActiveTab, openDrawerToTab, caseId, copyHistoryState, injectDrawerApi, registerApplyCaptureToEventMedia, applyCaptureToEventMedia, captureApplyNonce, eventMediaCaptureApplyReady, registerAddSharedSourceToEvent, addSharedSourceToEvent, eventSharedSourceAddReady, registerApplyTextToEventDescription, applyTextToEventDescription, eventDescriptionApplyReady]);
  const onAddToTimelineAndCloseDrawer = useCallback((event: import("./answer-item").EventFromChat) => {
    if (onAddToTimeline) {
      onAddToTimeline(event);
      closeDrawer();
    }
  }, [onAddToTimeline, closeDrawer]);
  return <ToolsDrawerCaptureApplyNonceContext.Provider value={captureApplyNonce} data-sentry-element="unknown" data-sentry-component="ToolsDrawerProviderInner" data-sentry-source-file="tools-drawer.tsx">
      <ToolsDrawerContext.Provider value={value} data-sentry-element="unknown" data-sentry-source-file="tools-drawer.tsx">
        <CopyHistoryListener addToCopyHistory={copyHistoryState.addToCopyHistory} data-sentry-element="CopyHistoryListener" data-sentry-source-file="tools-drawer.tsx" />
        <MediaSourceShareProviderWithDrawer caseId={caseId} onAddToTimeline={onAddToTimelineAndCloseDrawer} injectDrawerApi={injectDrawerApi} data-sentry-element="MediaSourceShareProviderWithDrawer" data-sentry-source-file="tools-drawer.tsx">
          {children}
          <ToolsDrawerShell caseId={caseId} data-sentry-element="ToolsDrawerShell" data-sentry-source-file="tools-drawer.tsx" />
        </MediaSourceShareProviderWithDrawer>
      </ToolsDrawerContext.Provider>
    </ToolsDrawerCaptureApplyNonceContext.Provider>;
}
function MediaSourceShareProviderWithDrawer({
  children,
  caseId,
  onAddToTimeline,
  injectDrawerApi
}: {
  children: React.ReactNode;
  caseId?: string;
  onAddToTimeline?: (event: import("./answer-item").EventFromChat) => void;
  injectDrawerApi: InjectedDrawerApi;
}) {
  return <MediaSourceShareProvider caseId={caseId} onAddToTimeline={onAddToTimeline} injectDrawer={injectDrawerApi} data-sentry-element="MediaSourceShareProvider" data-sentry-component="MediaSourceShareProviderWithDrawer" data-sentry-source-file="tools-drawer.tsx">
      {children}
    </MediaSourceShareProvider>;
}